<template>
  <div class="work-machine-panel" v-if="canView || canEdit || canDelete" v-cloak>
    <GridPanelHeader
      :title="$t('works.work_machines.name')"
      :show-add="canEdit"
      :show-filter="false"
      :grid-ref="$refs.workMachinesGrid"
      @filter="openFilterPanel"
      @columns="openColumnChooser"
      @export="exportGrid"
      @add="newObject"
      :show-refresh="true"
    />

    <GridFilter
      :visible="filterPanelVisible"
      :title="$t('common.filter')"
      @apply="applyFilters"
      @reset="resetFilters"
      @close="closeFilterPanel"
    >
      <div class="filter-field">
        <label>{{ $t("works.work_machines.name") }}</label>
        <input v-model="filters.name" :placeholder="$t('works.work_machines.name')" type="text" />
      </div>
      <div class="filter-field">
        <label>{{ $t("common.registration_number") }}</label>
        <input v-model="filters.registration_number" :placeholder="$t('common.registration_number')" type="text" />
      </div>
    </GridFilter>

    <div class="machine-alert-box alert alert-warning mt-2" v-if="apiConfig && unassignedRows.length > 0">
      <img src="~@/assets/images/desktop/icons/controls/alert.svg" class="icon-svg text-dark pr-2" />
      <span
        @click="syncWorkMachines"
        v-html="
          $t('works.work_machines.you_have_unassigned_wor_machines_link', {
            count: unassignedRows.length,
          })
        "
      />
      <div class="load text-center" v-if="syncLoading">
        <loader :loading="syncLoading" size="sm" />
      </div>
    </div>

    <DxDataGrid
      ref="workMachinesGrid"
      :data-source="workMachinesDataSource"
      :column-min-width="50"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :remote-operations="gridRemoteOperations"
      :element-attr="gridAttributes"
      @editing-start="onEditingStart"
      @exporting="onExporting"
      @option-changed="onOptionChanged"
    >
      <!-- Info icon column before name -->
      <DxColumn
        :width="50"
        alignment="left"
        :fixed="true"
        fixed-position="left"
        :show-in-column-chooser="false"
        cell-template="infoCellTemplate"
      />
      <template #infoCellTemplate="{ data }">
        <div class="icon-cell">
          <span
            class="dx-icon dx-icon-info smagria-col-icon clickable-icon info-icon"
            :title="$t('works.work_machines.machine_consume_history')"
            role="button"
            tabindex="0"
            @click="openMachineHistory(data.data)"
            @keydown.enter.prevent="openMachineHistory(data.data)"
            @keydown.space.prevent="openMachineHistory(data.data)"
          ></span>
        </div>
      </template>
      <DxColumn
        :width="50"
        alignment="left"
        :fixed="true"
        fixed-position="left"
        :show-in-column-chooser="false"
        cell-template="expirationAlertCell"
      />
      <template #expirationAlertCell="{ data }">
        <div class="icon-cell">
          <span
            v-if="data && data.data && data.data.closest_expiration_date"
            class="smagria-col-icon alert-icon clickable-icon"
            :class="expirationAlertClass(data.data)"
            :title="$t('works.work_machines.expiration_documents')"
            role="button"
            tabindex="0"
            @click="showExpirationDetails(data.data)"
            @keydown.enter.prevent="showExpirationDetails(data.data)"
            @keydown.space.prevent="showExpirationDetails(data.data)"
          >
            &#9888;
          </span>
        </div>
      </template>
      <DxColumn
        data-field="name"
        :caption="$t('common.name')"
        data-type="string"
        alignment="left"
        width="250"
        :allow-sorting="false"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="registration_number"
        :caption="$t('common.registration_number')"
        data-type="string"
        alignment="left"
        :allow-sorting="false"
        width="200"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="series_number"
        :caption="$t('common.serial_number')"
        data-type="string"
        alignment="left"
        :allow-sorting="false"
        width="160"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="machine_type"
        cell-template="machineTypeCell"
        :calculate-cell-value="machineTypeValue"
        :caption="$t('common.type')"
        data-type="string"
        alignment="left"
        :allow-sorting="false"
        :allow-filtering="false"
        :filter-operations="['contains']"
      />
      <template #machineTypeCell="{ data }">
        <span v-html="machineTypeCol(data.data)"></span>
      </template>
      <DxColumn
        data-field="closest_expiration_date"
        cell-template="closestExpirationCell"
        :caption="$t('common.expiration_date')"
        data-type="date"
        alignment="left"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['=']"
        sort-order="asc"
        :sort-index="0"
      />
      <template #closestExpirationCell="{ data }">
        <span>{{ formatClosestExpiration(data.data && data.data.closest_expiration_date) }}</span>
      </template>
      <DxColumn
        data-field="work_type.name"
        :caption="$t('common.work_type')"
        data-type="string"
        alignment="left"
        :allow-sorting="false"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="total_cost"
        :caption="$t('works.work_machines.total_cost')"
        data-type="number"
        alignment="left"
        :allow-sorting="false"
        :allow-filtering="false"
      />
      <DxColumn
        data-field="department.name"
        :caption="$t('common.owner')"
        data-type="string"
        alignment="left"
        :allow-sorting="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="remote_id"
        :caption="$t('remote_id')"
        data-type="string"
        alignment="left"
        :allow-sorting="false"
        :filter-operations="['contains']"
      />

      <DxColumn
        data-field="is_active"
        :caption="$t('common.active')"
        data-type="number"
        alignment="left"
        :allow-sorting="false"
        :allow-filtering="true"
        :filter-operations="['=']"
        :min-width="140"
        cell-template="isActiveViewTemplate"
        :calculateCellValue="(row) => getActiveStatusName(row.is_active)"
      >
        <DxLookup :data-source="active_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #isActiveViewTemplate="{ data }">
        <span>{{ getActiveStatusName(data.data.is_active) }}</span>
      </template>

      <DxColumn
        alignment="left"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        :width="160"
        cell-template="addCostCell"
      />
      <template #addCostCell="{ data }">
        <DxButton
          class="smagria-col-btn"
          icon="add"
          styling-mode="contained"
          type="normal"
          :text="$t('works.work_machines.add_cost')"
          :disabled="!canEdit"
          @click="openCostPopup(data.data)"
        />
      </template>

      <DxColumn
        alignment="left"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        :width="180"
        cell-template="stockConsumCell"
      />
      <template #stockConsumCell="{ data }">
        <DxButton
          class="smagria-col-btn"
          icon="add"
          styling-mode="contained"
          type="normal"
          :text="$t('works.work_machines.stock_consum')"
          :disabled="!canEdit"
          @click="openStockConsum(data.data)"
        />
      </template>

      <DxColumn
        :width="60"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="dynamicMenuCell"
      />
      <template #dynamicMenuCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs.workMachinesGrid"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="allowDeleting"
        />
      </template>

      <!-- Built-in edit/delete icons via DxEditing -->
      <DxEditing mode="row" :allowUpdating="false" :allowAdding="false" :allowDeleting="false" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('works.work_machines.name'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>

      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'WorkMachines'" />
      <DxSummary>
        <DxTotalItem column="total_cost" summary-type="sum" :display-format="$t('common.total') + ': {0}'" value-format="#,##0.##" />
      </DxSummary>
    </DxDataGrid>

    <work-machine-cost-popup
      :visible="costPopupVisible"
      :machine="selectedMachine"
      :loading="costPopupLoading"
      :date-format="getDxGridDefaultDateFormat"
      @save-cost="saveCost"
      @close="closeCostPopup"
    />
    <!-- Machine consume history dialog -->
    <machine-consume-history :showPopup="machineHistoryDialogVisibility" :machine="selectedMachine" @hidden="hideMachineConsumeHistory" />
    <work-machine-expiration-popup :visible.sync="expirationPopupVisible" :data="expirationPopupContent" @close="closeExpirationPopup" />
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { canDelete, canEdit, canView, permissions, permissionTypes } from "@/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import { types } from "@/helpers/ImportTypes";
import { sourceTypes } from "@/helpers/ImportSourceTypes";
import { handleError } from "@/helpers/generalActions";
import Loader from "@/components/Loader.vue";
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxEditing,
  DxTexts,
  DxExport,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxItem,
  DxSummary,
  DxTotalItem,
  DxLookup,
} from "devextreme-vue/data-grid";
import DxButton from "devextreme-vue/button";
import MachineConsumeHistory from "~/pages/works/work-machines/dialogs/MachineConsumHistory.vue";
import WorkMachineExpirationPopup from "./partials/WorkMachineExpirationPopup.vue";
import WorkMachineCostPopup from "./dialogs/WorkMachineCostPopup.vue";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import { ConsumTypes } from "@/pages/stock-mobile/dialogs/partials/consum.js";

const PRESELECTED_MACHINE_STORAGE_KEY = "da_preselected_machine_for_stock_consum";

export default {
  name: "WorkMachines",
  middleware: ["auth", "check-company-admin"],
  data() {
    return {
      filterPanelVisible: false,
      active_list: [
        { id: 1, label: this.$t("common.active") },
        { id: 0, label: this.$t("common.inactive") },
      ],
      filters: {
        name: "",
        registration_number: "",
      },
      loading: false,
      syncLoading: false,
      machineHistoryDialogVisibility: false,
      selectedMachine: null,
      expirationPopupVisible: false,
      expirationPopupContent: {
        name: "",
        date: "",
        notes: "",
        daysRemaining: "",
      },
      costPopupVisible: false,
      costPopupLoading: false,
      workMachinesDataSource: new DataSource({
        store: new CustomStore({
          key: "uuid",
          load: async (loadOptions) => {
            let dataSource = {
              data: [],
              totalCount: 0,
            };

            /*const hasLocalData = Array.isArray(this.workMachines) && this.workMachines.length > 0;
            const hasGrouping = Array.isArray(loadOptions.group) && loadOptions.group.length > 0;
            const hasGroupSort = Array.isArray(loadOptions.sort) && loadOptions.sort.some((s) => s.isExpanded || s.groupIndex >= 0);
            // When grouping is applied, serve already loaded data locally to avoid extra API calls.
            // Also clear grouping sort to avoid backend sorting requests.
            if (hasGrouping && hasLocalData) {
              if (hasGroupSort) {
                loadOptions.sort = [];
              }
              dataSource.data = this.workMachines;
              dataSource.totalCount = this.pagination?.total ?? this.workMachines.length;
              return dataSource;
            }*/

            try {
              const pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
              const pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;
              const payload = {
                page: pageNumber,
                limit: pageLimit,
                filter: loadOptions.filter,
                sort: loadOptions.sort,
                all_pages_summary_calculation: 1,
              };

              await this.getWorkMachines(payload).then((response) => {
                dataSource.data = response.data;
                this.lastLoadedItems = Array.isArray(response.data) ? response.data : [];
                dataSource.totalCount = response?.pagination?.total ?? 1;
                dataSource.summary = [response?.summary?.total_cost_all_over_pages ?? 0];
              });

              return dataSource;
            } catch (error) {
              console.error("Error fetching work machines:", error);
              throw error;
            }
          },
          remove: async (key) => {
            await this.deleteWorkMachine({ uuid: key });
          },
        }),
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
        summary: true,
        /*grouping: false,
        groupPaging: true,*/
      },
      pageSizes: [15, 50, 100],
      lastLoadedItems: [],
    };
  },
  components: {
    Loader,
    Forbidden,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxEditing,
    DxTexts,
    DxExport,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxButton,
    MachineConsumeHistory,
    WorkMachineExpirationPopup,
    WorkMachineCostPopup,
    GridPanelHeader,
    GridFilter,
    DxItem,
    DxSummary,
    DxTotalItem,
    DxActionMenu,
    DxLookup,
  },
  computed: {
    ...mapGetters({
      importConfigurations: "importConfig/importConfigurations",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    gridAttributes() {
      return {
        class: "smagria-grid work-machine-list-grid" + ((this.unassignedRows?.length ?? 0) > 0 && this.apiConfig ? " with-alert" : ""),
      };
    },
    config() {
      return {
        permission: permissions.WORK_MACHINES,
        permission_type: permissionTypes.access,
        edit: {
          url: "/works/work-machines/{uuid}",
        },
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    // headers replaced by DxDataGrid columns
    title() {
      return this.$t("works.work_machines.name");
    },
    unassignedRows() {
      if (Array.isArray(this.lastLoadedItems)) {
        return this.lastLoadedItems.filter((item) => !item.remote_id);
      }
      return [];
    },
    apiConfig() {
      return this.importConfigurations?.find(
        (config) =>
          config.import_type === "SGPS_DASC_IMPORT" &&
          config.is_active === 1 &&
          config.source_type === "API_IMPORT" &&
          config.source_config_uuid,
      );
    },
    lastPage() {
      const inst = this.$refs?.workMachinesGrid?.instance;
      return inst ? inst.pageCount() - 1 : 0;
    },
  },
  methods: {
    openFilterPanel() {
      this.filterPanelVisible = true;
    },
    closeFilterPanel() {
      this.filterPanelVisible = false;
    },
    applyFilters() {
      const conditions = [];
      if (this.filters.name) {
        conditions.push(["name", "contains", this.filters.name]);
      }
      if (this.filters.registration_number) {
        conditions.push(["registration_number", "contains", this.filters.registration_number]);
      }

      const instance = this.$refs?.workMachinesGrid?.instance;
      if (!instance) {
        this.closeFilterPanel();
        return;
      }

      if (conditions.length === 0) {
        instance.clearFilter();
      } else {
        const filterExpr = conditions.reduce((acc, curr) => (acc ? [acc, "and", curr] : curr), null);
        instance.filter(filterExpr);
      }
      instance.refresh();
      this.closeFilterPanel();
    },
    resetFilters() {
      this.filters.name = "";
      this.filters.registration_number = "";
      const instance = this.$refs?.workMachinesGrid?.instance;
      if (instance) {
        instance.clearFilter();
        instance.refresh();
      }
      this.closeFilterPanel();
    },
    ...mapActions({
      syncTracktorApiVehicles: "importConfig/syncTracktorApiVehicles",
      getImportConfigurations: "importConfig/getImportConfigurations",
      getWorkMachines: "works-work-machines/getWorkMachines",
      deleteWorkMachine: "works-work-machines/deleteWorkMachine",
      createWorkMachineCost: "works-work-machines/createWorkMachineCost",
    }),
    newObject() {
      this.$router.push({
        path: "/works/work-machines/add",
      });
    },
    disableDeleteIf(row) {
      return !row.is_deletable;
    },
    machineTypeCol(col) {
      const label = this.machineTypeLabel(col);
      return '<span>' + label.replace(/\n/g, "<br/>") + "</span>";
    },
    machineTypeLabel(col) {
      if (!col) return "";
      const parts = [];
      if (col.autopropulsat) parts.push(this.$t("works.work_machines.autopropulsat"));
      if (col.equipment) parts.push(this.$t("works.work_machines.equipment"));
      if (col.is_dataller === 1) parts.push(this.$t("works.work_machines.dataller"));
      return parts.join("\n");
    },
    machineTypeValue(row) {
      return this.machineTypeLabel(row);
    },
    getActiveStatusName(value) {
      return this.active_list.find((item) => item.id == value)?.label ?? "";
    },
    expirationAlertClass(row) {
      if (!row || !row.closest_expiration_date) return "";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const exp = new Date(row.closest_expiration_date);
      exp.setHours(0, 0, 0, 0);
      return exp < today ? "alert-expired" : "alert-upcoming";
    },
    showExpirationDetails(row) {
      if (!row || !row.closest_expiration_date) return;
      const target = this.normalizeDate(row.closest_expiration_date);
      const match = Array.isArray(row.expiration_documents)
        ? row.expiration_documents.find((doc) => this.normalizeDate(doc.expiration_date) === target)
        : null;
      if (!match) return;
      this.expirationPopupContent = {
        name: match.name || "-",
        date: this.formatClosestExpiration(match.expiration_date),
        notes: match.notes || "-",
        daysRemaining: this.computeDaysRemaining(match.expiration_date),
      };
      this.expirationPopupVisible = true;
    },
    normalizeDate(value) {
      if (!value) return "";
      try {
        return format(new Date(value), this.getDxGridDefaultDateFormat);
      } catch (e) {
        return "";
      }
    },
    computeDaysRemaining(value) {
      if (!value) return "-";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(value);
      target.setHours(0, 0, 0, 0);
      const diffMs = target.getTime() - today.getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    closeExpirationPopup() {
      this.expirationPopupVisible = false;
      this.expirationPopupContent = { name: "", date: "", notes: "", daysRemaining: "" };
    },
    formatClosestExpiration(value) {
      if (!value) return "-";
      try {
        return format(new Date(value), this.getDxGridDefaultDateFormat);
      } catch (e) {
        return value;
      }
    },
    allowDeleting(rowData) {
      //const rowData = component && component.row ? component.row.data : null;
      return this.canDelete && rowData && !this.disableDeleteIf(rowData);
    },
    onEditingStart(e) {
      // Use built-in edit icon, but route to edit page like weighing page
      e.cancel = true;
      if (e && e.data) {
        this.onEdit(e.data);
      }
    },
    onExporting(e) {
      e.fileName = "WorkMachines_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("WorkMachines");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if (!gridCell?.data) return;
            if (gridCell?.column?.dataField === "is_active") {
              excelCell.value = this.getActiveStatusName(gridCell?.data?.is_active);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    openCostPopup(machine) {
      if (!machine || !machine.uuid) return;
      this.selectedMachine = machine;
      this.costPopupVisible = true;
    },
    openStockConsum(machine) {
      if (!machine || !machine.uuid) return;

      try {
        window.sessionStorage.setItem(
          PRESELECTED_MACHINE_STORAGE_KEY,
          JSON.stringify({
            uuid: machine.uuid,
            name: machine.name,
            registration_number: machine.registration_number,
          }),
        );
      } catch (e) {
        // ignore storage write errors
      }

      this.$router.push({
        path: "/stock-mobile",
        query: {
          fromApplyArticles: "1",
          startConsum: "1",
          returnTo: "/works/work-machines",
          consumOption: ConsumTypes.Machinery,
          preselectMachine: "1",
        },
      });
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        const instance = this.$refs?.workMachinesGrid?.instance;
        if (instance) {
          // reset to first page and clear cache so backend fetch starts from page 1 with new limit
          instance.option("paging.pageIndex", 0);
        }
        this.lastLoadedItems = [];
        this.workMachinesDataSource?.reload?.();
      }
    },
    openColumnChooser() {
      const instance = this.$refs.workMachinesGrid && this.$refs.workMachinesGrid.instance;
      if (instance) {
        instance.showColumnChooser();
      }
    },
    exportGrid() {
      const instance = this.$refs.workMachinesGrid && this.$refs.workMachinesGrid.instance;
      if (!instance) return;

      this.onExporting({ component: instance, format: "xlsx", fileName: "WorkMachines_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss") });
    },
    async saveCost(payload) {
      if (this.costPopupLoading) return;
      if (!payload || !payload.machine_uuid) return;
      this.costPopupLoading = true;
      try {
        await this.createWorkMachineCost(payload);
        this.costPopupVisible = false;
        this.workMachinesDataSource.reload();
      } catch (e) {
        handleError(this, e);
      } finally {
        this.costPopupLoading = false;
      }
    },
    closeCostPopup() {
      this.costPopupVisible = false;
    },
    openMachineHistory(machine) {
      this.selectedMachine = machine;
      this.machineHistoryDialogVisibility = true;
    },
    hideMachineConsumeHistory(payload) {
      this.machineHistoryDialogVisibility = false;
      this.selectedMachine = null;
      if (payload && payload.deleted) {
        this.workMachinesDataSource.reload();
      }
    },
    onEdit(row) {
      if (!row || !row.uuid) return;
      this.$router.push({ path: `/works/work-machines/${row.uuid}` });
    },
    syncWorkMachines(event) {
      const vm = this;
      if (event.target.classList.contains("clickable")) {
        vm.syncLoading = true;
        this.syncTracktorApiVehicles({
          import_configuration_uuid: this.apiConfig["uuid"] ?? null,
        })
          .then((resp) => {
            this.workMachinesDataSource.reload();
          })
          .catch((e) => {
            handleError(vm, e);
          })
          .finally(() => {
            vm.syncLoading = false;
          });
      }
    },
  },
  mounted() {
    this.getImportConfigurations({
      import_type: types.SGPS_DASC_IMPORT.id,
      source_type: sourceTypes.API_IMPORT.id,
    });
  },
};
</script>

<style scoped lang="scss">
.work-machine-list-grid.with-alert {
  /* reduce height when the alert box is visible */
  height: calc(95vh - 210px);
}
</style>
<style lang="scss">
@import "@/assets/scss/components/dxSmagriaGrid.scss";
</style>
